/**
 * walletCache — instant first-paint for the parent's wallet balance.
 *
 * We stash the last-known `wallet_available_pennies` (and a few peer fields)
 * into `sessionStorage` so that during the short window between mount and
 * `/auth/me` returning, the Household Wallet card can render the previously
 * seen balance rather than a jarring £0.00.
 *
 * Only pennies are cached — no PII, no bank data, no Stripe tokens.
 * `sessionStorage` (not `localStorage`) so the cache is scoped to the tab
 * and evaporates on close for extra safety.
 */
const KEY = "mttApp:walletCache";

export function readWalletCache() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    // Reject anything older than 10 minutes so a long-idle tab doesn't
    // display stale numbers.
    const at = new Date(parsed.at || 0).getTime();
    if (!Number.isFinite(at) || Date.now() - at > 10 * 60 * 1000) return null;
    return parsed;
  } catch (_) { return null; }
}

export function writeWalletCache(user) {
  if (!user || typeof user !== "object") return;
  try {
    sessionStorage.setItem(
      KEY,
      JSON.stringify({
        parent_id: user.id || user.parent_id || null,
        wallet_available_pennies: user.wallet_available_pennies ?? 0,
        wallet_pending_pennies: user.wallet_pending_pennies ?? 0,
        wallet_payout_requested_pennies: user.wallet_payout_requested_pennies ?? 0,
        bank_last4: user.bank_last4 || null,
        bank_name: user.bank_name || null,
        at: new Date().toISOString(),
      })
    );
  } catch (_) { /* sessionStorage unavailable — ignore */ }
}

export function clearWalletCache() {
  try { sessionStorage.removeItem(KEY); } catch (_) { /* noop */ }
}
